/**
 * Mock API Service for Demo Mode
 *
 * This service intercepts API calls in demo mode and returns static JSON data
 * instead of making real HTTP requests.
 */

const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';

// Cache for loaded mock data
const mockDataCache = {};

/**
 * Load mock data from public/mock-data folder
 */
async function loadMockData(filename) {
  if (mockDataCache[filename]) {
    return mockDataCache[filename];
  }

  try {
    const response = await fetch(`/mock-data/${filename}`);
    const data = await response.json();
    mockDataCache[filename] = data;
    return data;
  } catch (error) {
    console.error(`Failed to load mock data: ${filename}`, error);
    return null;
  }
}

/**
 * Simulate network delay for realism
 */
function delay(ms = 300) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Mock API handlers for different endpoints
 */
const mockHandlers = {
  // Routes endpoints
  '/routes/get-by-date': async (params) => {
    await delay(400);
    const routes = await loadMockData('routes.json');

    console.log('[DEMO MODE] /routes/get-by-date called with params:', params);
    console.log('[DEMO MODE] Loaded routes:', routes?.length, 'routes');

    // Filter routes by date if provided
    // Frontend sends date as "YYYY-MM-DD" but routes store ISO dates "YYYY-MM-DDT00:00:00.000Z"
    let filteredRoutes = routes;
    if (params?.date) {
      filteredRoutes = routes.filter((r) => r.date?.startsWith(params.date));
      console.log('[DEMO MODE] Filtered by date', params.date, ':', filteredRoutes?.length, 'routes');
    }

    console.log(
      '[DEMO MODE] Returning routes with polylines:',
      filteredRoutes?.map((r) => ({ id: r.id, hasPolyline: !!r.polyline, polylineLength: r.polyline?.length })),
    );

    // Backend returns just the array of routes
    return filteredRoutes || [];
  },

  '/routes/get-log-gmpro-by-date': async () => {
    await delay(200);
    return { logs: [] };
  },

  '/routes/dispatch': async () => {
    await delay(500);
    return { success: true, message: 'Routes dispatched (demo mode - no action taken)' };
  },

  '/routes/optimize': async () => {
    await delay(800);
    return { success: true, message: 'Optimization queued (demo mode - no action taken)' };
  },

  '/routes/optimize-gmpro': async () => {
    await delay(800);
    return { success: true, message: 'Optimization queued (demo mode - no action taken)' };
  },

  '/routes': async () => {
    await delay(300);
    const routes = await loadMockData('routes.json');
    return {
      routes: routes || [],
      pagination: {
        totalItems: routes?.length || 0,
        totalPages: 1,
        currentPage: 1,
      },
    };
  },

  '/routes/:id/reorder-orders': async () => {
    await delay(400);
    return { success: true, message: 'Orders reordered (demo mode - no action taken)' };
  },

  // Orders endpoints
  '/orders': async () => {
    await delay(300);
    const orders = await loadMockData('orders.json');
    return {
      orders: orders || [],
      pagination: {
        totalItems: orders?.length || 0,
        totalPages: 1,
        currentPage: 1,
      },
    };
  },

  '/orders/:id': async (params) => {
    await delay(200);
    const orders = await loadMockData('orders.json');
    const orderId = parseInt(params.id);
    const order = orders.find((o) => o.id === orderId);
    return order || null;
  },

  '/orders/change-to-draft': async () => {
    await delay(300);
    return { success: true, message: 'Order status changed (demo mode - no action taken)' };
  },

  '/orders/change-to-unassigned': async () => {
    await delay(300);
    return { success: true, message: 'Order status changed (demo mode - no action taken)' };
  },

  // Drivers endpoints
  '/drivers': async () => {
    await delay(200);
    const drivers = await loadMockData('drivers.json');
    return {
      drivers: drivers || [],
      pagination: {
        totalItems: drivers?.length || 0,
        totalPages: 1,
        currentPage: 1,
      },
    };
  },

  // Fleets endpoints
  '/fleets': async () => {
    await delay(200);
    const fleets = await loadMockData('fleets.json');
    return {
      fleets: fleets || [],
      pagination: {
        totalItems: fleets?.length || 0,
        totalPages: 1,
        currentPage: 1,
      },
    };
  },

  // Hubs endpoints
  '/hubs': async () => {
    await delay(200);
    const hubs = await loadMockData('hubs.json');
    return {
      hubs: hubs || [],
      pagination: {
        totalItems: hubs?.length || 0,
        totalPages: 1,
        currentPage: 1,
      },
    };
  },

  '/hubs/:id': async (params) => {
    await delay(200);
    const hubs = await loadMockData('hubs.json');
    const fleets = await loadMockData('fleets.json');
    const hubId = parseInt(params.id);
    const hub = hubs.find((h) => h.id === hubId);

    if (!hub) return null;

    // Attach fleets belonging to this hub
    const hubFleets = (fleets || []).filter((f) => f.hubId === hubId);
    return {
      ...hub,
      fleets: hubFleets,
    };
  },

  // Scheduler Forms endpoints
  '/scheduler-forms': async () => {
    await delay(200);
    const forms = await loadMockData('scheduler-forms.json');
    return {
      schedulerForms: forms || [],
      pagination: {
        totalItems: forms?.length || 0,
        totalPages: 1,
        currentPage: 1,
      },
    };
  },

  '/scheduler-forms/synchronize': async () => {
    await delay(400);
    const forms = await loadMockData('scheduler-forms.json');
    return forms;
  },

  // Order Tickets endpoints
  '/order-tickets': async () => {
    await delay(200);
    return [];
  },

  // Auth endpoints
  '/auth/login': async () => {
    await delay(500);
    return {
      user: {
        id: 1,
        email: 'demo@ovoplanner.com',
        name: 'Demo User',
        role: 'admin',
      },
      token: 'demo-token-12345',
    };
  },

  '/auth/me': async () => {
    await delay(200);
    return {
      id: 1,
      email: 'demo@ovoplanner.com',
      name: 'Demo User',
      role: 'admin',
    };
  },
};

/**
 * Match endpoint pattern with wildcards
 */
function matchEndpoint(endpoint) {
  for (const pattern in mockHandlers) {
    // Convert pattern to regex (replace :id with \d+)
    const regexPattern = pattern.replace(/:id/g, '(\\d+)').replace(/\//g, '\\/');
    const regex = new RegExp(`^${regexPattern}$`);

    if (regex.test(endpoint)) {
      return pattern;
    }
  }
  return null;
}

/**
 * Extract params from endpoint with wildcards
 */
function extractParams(endpoint, pattern) {
  const regexPattern = pattern.replace(/:id/g, '(\\d+)').replace(/\//g, '\\/');
  const regex = new RegExp(`^${regexPattern}$`);
  const match = endpoint.match(regex);

  if (match && match[1]) {
    return { id: match[1] };
  }
  return {};
}

/**
 * Main mock API function
 * Intercepts API calls and returns mock data
 */
export async function mockApiRequest(endpoint, opts) {
  if (!isDemoMode) {
    return null; // Not in demo mode, let real request proceed
  }

  console.log('[DEMO MODE] Intercepting API call:', endpoint, opts);

  const matchedPattern = matchEndpoint(endpoint);
  const handler = mockHandlers[matchedPattern];

  if (!handler) {
    console.warn('[DEMO MODE] No mock handler for endpoint:', endpoint);
    return { response: {} };
  }

  try {
    const pathParams = extractParams(endpoint, matchedPattern);
    const combinedParams = { ...pathParams, ...opts?.data, ...opts?.params };
    const data = await handler(combinedParams);

    return { response: data };
  } catch (error) {
    console.error('[DEMO MODE] Mock API error:', error);
    return { error: { message: 'Mock API error' } };
  }
}

export { isDemoMode };
